<template>
    <ModalComponent :is-open="isModalOpen" :show-footer-buttons="true" :title="modalTitle" size="2xl" position="center" @update:isOpen="handleClose">
        <div class="p-4 space-y-4">
            <!-- Username -->
            <div>
                <label class="block text-sm font-medium text-gray-700"> Username <span v-if="!unchangedUser" class="text-red-500">*</span> </label>
                <input
                    type="text"
                    autocomplete="off"
                    v-model="currentUser.username"
                    :disabled="!!unchangedUser"
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-xs sm:text-sm disabled:bg-gray-50 disabled:text-gray-500 disabled:border-gray-200"
                    maxlength="24"
                    placeholder="Enter username" />
                <p v-if="usernameError" class="mt-1 text-sm text-red-600">{{ usernameError }}</p>
            </div>
            <!-- Password -->
            <div>
                <label class="block text-sm font-medium text-gray-700">
                    {{ unchangedUser ? 'Password (optional)' : 'Password' }} <span v-if="!unchangedUser" class="text-red-500">*</span>
                </label>
                <input
                    type="password"
                    autocomplete="new-password"
                    v-model="currentUser.password"
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-xs sm:text-sm"
                    :placeholder="unchangedUser ? 'Enter new password to change it' : 'Enter password'" />
                <p v-if="passwordError" class="mt-1 text-sm text-red-600">{{ passwordError }}</p>
            </div>
            <!-- Whitelisted IPs -->
            <div>
                <label class="block text-sm font-medium text-gray-700">Whitelisted IPs</label>
                <div class="flex flex-wrap gap-2 mt-1">
                    <span
                        v-for="(ip, idx) in ipsArray"
                        :key="idx"
                        class="inline-flex items-center bg-gray-100 text-gray-800 text-xs rounded px-2 py-1">
                        {{ ip }}
                        <XMarkIcon @click="removeIp(idx)" class="ml-1 h-4 w-4 cursor-pointer text-gray-600 hover:text-red-600" />
                    </span>
                </div>
                <input
                    type="text"
                    v-model="newIpInput"
                    @keydown="onIpKeyDown"
                    @blur="addIpFromInput"
                    class="mt-2 block w-full rounded-md border-gray-300 shadow-xs sm:text-sm"
                    placeholder="Enter IP/CIDR and press Enter or comma" />
                <p v-if="ipError" class="mt-1 text-sm text-red-600">{{ ipError }}</p>
            </div>
            <!-- Kafka ACLs -->
            <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Kafka ACLs (optional)</label>
                <div class="grid grid-cols-12 gap-2 mt-2 mb-1 font-medium text-gray-700">
                    <div class="col-span-4 text-sm">Name</div>
                    <div class="col-span-2 text-sm">Resource</div>
                    <div class="col-span-3 text-sm">Operation</div>
                    <div class="col-span-2 text-sm">Pattern Type</div>
                    <div class="col-span-1 text-center text-sm">Action</div>
                </div>
                <div v-if="currentUser.kafka_acls.length" class="max-h-80 overflow-y-auto mb-2">
                    <div v-for="(acl, idx) in currentUser.kafka_acls" :key="idx" class="grid grid-cols-12 gap-2 items-center mb-2">
                        <input type="text" v-model="acl.name" class="col-span-4 rounded-md border-gray-300 shadow-xs sm:text-sm" placeholder="Name" />
                        <select v-model="acl.resource" class="col-span-2 rounded-md border-gray-300 shadow-xs sm:text-sm">
                            <option v-for="rt in ResourceTypeOptions" :key="rt.value" :value="rt.value">
                                {{ rt.label }}
                            </option>
                        </select>
                        <select v-model="acl.operation" class="col-span-3 rounded-md border-gray-300 shadow-xs sm:text-sm">
                            <option v-for="op in getOperationOptionsForResource(acl.resource)" :key="op.value" :value="op.value">
                                {{ op.label }}
                            </option>
                        </select>
                        <select v-model="acl.resource_pattern_type" class="col-span-2 rounded-md border-gray-300 shadow-xs sm:text-sm">
                            <option v-for="pt in ResourcePatternTypeOptions" :key="pt.value" :value="pt.value">
                                {{ pt.label }}
                            </option>
                        </select>
                        <button type="button" class="col-span-1 flex justify-center p-2 text-gray-600 hover:text-red-600" @click="removeAcl(idx)">
                            <TrashIcon class="h-5 w-5" />
                        </button>
                    </div>
                </div>
                <button type="button" class="text-sm text-cyan-900 hover:underline" @click="addAcl">+ Add ACL</button>
                <span class="inline-flex items-center ml-4">
                    <button type="button" class="text-sm text-cyan-900 hover:underline" @click="openCsvUpload">Import .CSV</button>
                    <Tooltip tooltipText="CSV must have headers: resource, name, operation, resource_pattern_type" position="top" class="ml-2" />
                </span>
                <input ref="fileInputCsv" type="file" accept=".csv" class="hidden" @change="loadAclsFromCsv" />
            </div>
        </div>
        <template #footer>
            <div class="flex justify-end space-x-2">
                <button
                    type="button"
                    @click="handleCancel"
                    class="bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-md py-2 px-4 mb-2 w-full min-w-[88px]">
                    Cancel
                </button>
                <LoadingBtn :is-loading="saveIsLoading" @click="handleSave" :can-save="enableSave"> Save </LoadingBtn>
            </div>
        </template>
    </ModalComponent>
</template>

<script setup>
import { ref, watch, computed } from 'vue';
import isEqual from 'lodash/isEqual';
import ModalComponent from '@/components/global/modals/Modal.vue';
import LoadingBtn from '@/components/global/buttons/loading-btn.vue';
import Tooltip from '@/components/Tooltip.vue';
import { TrashIcon, XMarkIcon } from '@heroicons/vue/24/solid';
import { useLoaderStore } from '@/stores/loader.store';
import Papa from 'papaparse';

const ACLOperationOptions = [
    { label: 'ALL', value: 'ALL' },
    { label: 'WRITE', value: 'WRITE' },
    { label: 'READ', value: 'READ' },
    { label: 'ALTER', value: 'ALTER' },
    { label: 'ALTER_CONFIGS', value: 'ALTER_CONFIGS' },
    { label: 'CREATE', value: 'CREATE' },
    { label: 'DELETE', value: 'DELETE' },
    { label: 'DESCRIBE', value: 'DESCRIBE' },
    { label: 'DESCRIBE_CONFIGS', value: 'DESCRIBE_CONFIGS' }
];
const ResourcePatternTypeOptions = [
    { label: 'LITERAL', value: 'LITERAL' },
    { label: 'PREFIXED', value: 'PREFIXED' }
];

const ResourceTypeOptions = [
    { label: 'TOPIC', value: 'TOPIC' },
    { label: 'GROUP', value: 'GROUP' }
];
const groupOperationValues = ['DELETE', 'DESCRIBE', 'READ'];
const getOperationOptionsForResource = (resource) =>
    resource === 'GROUP' ? ACLOperationOptions.filter((op) => groupOperationValues.includes(op.value)) : ACLOperationOptions;

const props = defineProps({
    isOpen: { type: Boolean, default: false },
    user: { type: Object, default: null }
});
const emit = defineEmits(['update:isOpen', 'save']);

const isModalOpen = ref(false);
const unchangedUser = ref(null);
const currentUser = ref({ username: '', password: '', whitelist_ips: '', kafka_acls: [] });
const ipError = ref('');
const usernameError = ref('');
const passwordError = ref('');

const ipsArray = ref([]);
const newIpInput = ref('');
const cidrRegex = /^((25[0-5]|2[0-4]\d|[01]?\d?\d)\.){3}(25[0-5]|2[0-4]\d|[01]?\d?\d)\/(?:[0-9]|[1-2]\d|3[0-2])$/;

const addIpFromInput = () => {
    if (!newIpInput.value) return;
    const values = newIpInput.value
        .split(',')
        .map((v) => v.trim())
        .filter((v) => v);
    for (const val of values) {
        if (!cidrRegex.test(val)) {
            ipError.value = `Invalid IP or CIDR format: ${val}`;
        } else if (!ipsArray.value.includes(val)) {
            ipsArray.value.push(val);
            ipError.value = '';
        }
    }
    newIpInput.value = '';
};

const onIpKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
        e.preventDefault();
        addIpFromInput();
    }
};

const removeIp = (idx) => {
    ipsArray.value.splice(idx, 1);
};

watch(
    () => currentUser.value.username,
    (val) => {
        if (!val || unchangedUser.value) {
            usernameError.value = '';
        } else {
            const usernameRegex = /^(?=.{1,24}$)(?!-)(?!.*-$)[A-Za-z0-9-]+$/;
            usernameError.value = usernameRegex.test(val)
                ? ''
                : 'Username must be 1-24 characters long, contain only letters, numbers or hyphens, and cannot start or end with a hyphen.';
        }
    }
);

const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;

watch(
    () => currentUser.value.password,
    (val) => {
        if (!val) {
            passwordError.value = '';
        } else {
            passwordError.value = passwordRegex.test(val)
                ? ''
                : 'Password must be at least 8 characters and include uppercase, lowercase, number, and special character.';
        }
    }
);

const loaderStore = useLoaderStore();
const saveIsLoading = computed(() => loaderStore.isLoading('kafkaUsers.add') || loaderStore.isLoading('kafkaUsers.update'));

// Initialize modal state (new or edit) whenever it opens
watch(
    () => props.isOpen,
    (val) => {
        isModalOpen.value = val;
        if (val) {
            if (props.user) {
                unchangedUser.value = { ...props.user };
                currentUser.value = {
                    username: props.user.username,
                    password: '',
                    whitelist_ips: props.user.whitelist_ips,
                    kafka_acls: props.user.kafka_acls
                        ? props.user.kafka_acls.map((a) => ({
                            resource: a.resource?.toUpperCase() || 'TOPIC',
                            name: a.name ?? a.topic_name,
                            operation: a.operation?.toUpperCase(),
                            resource_pattern_type: a.resource_pattern_type?.toUpperCase()
                        }))
                        : []
                };
                ipsArray.value = props.user.whitelist_ips
                    ? props.user.whitelist_ips
                        .split(',')
                        .map((v) => v.trim())
                        .filter((v) => v)
                    : [];
            } else {
                unchangedUser.value = null;
                currentUser.value = { username: '', password: '', whitelist_ips: '', kafka_acls: [] };
                ipsArray.value = [];
            }
            newIpInput.value = '';
            ipError.value = '';
            usernameError.value = '';
        }
    }
);

const modalTitle = computed(() => (props.user ? 'Edit Kafka User' : 'Create Kafka User'));

const enableSave = computed(() => {
    if (!currentUser.value.username.trim()) return false;
    if (!props.user && !currentUser.value.password) return false;
    if (ipError.value) return false;
    if (usernameError.value) return false;
    if (passwordError.value) return false;
    for (const acl of currentUser.value.kafka_acls) {
        if (!acl.name.trim() || !acl.resource || !acl.operation || !acl.resource_pattern_type) return false;
    }
    if (props.user) {
        const newIps = ipsArray.value.join(',');
        const origIps = props.user.whitelist_ips || '';
        if (!currentUser.value.password && newIps === origIps && isEqual(currentUser.value.kafka_acls, props.user.kafka_acls || [])) {
            return false;
        }
    }
    return true;
});

const handleClose = (val) => {
    isModalOpen.value = val;
    if (!val) emit('update:isOpen', false);
};
const handleCancel = () => emit('update:isOpen', false);

const addAcl = () => {
    currentUser.value.kafka_acls.push({ resource: 'TOPIC', name: '', operation: 'READ', resource_pattern_type: 'LITERAL' });
};
const removeAcl = (idx) => {
    currentUser.value.kafka_acls.splice(idx, 1);
};

const fileInputCsv = ref(null);

const openCsvUpload = () => {
    fileInputCsv.value.value = '';
    fileInputCsv.value.click();
};

const loadAclsFromCsv = (e) => {
    const file = e.target.files[0];
    if (!file?.name.toLowerCase().endsWith('.csv')) {
        alert('Please upload a valid CSV file.');
        return;
    }
    Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete({ data }) {
            const rows = data;
            const newAcls = [];
            for (const row of rows) {
                const resource = row.resource?.trim().toUpperCase();
                const name = row.name?.trim();
                const op = row.operation?.trim().toUpperCase();
                const pattern = row.resource_pattern_type?.trim().toUpperCase();
                if (!resource || !name || !op || !pattern) {
                    alert("Invalid CSV format. Ensure 'resource', 'name', 'operation', and 'resource_pattern_type' are provided for each row.");
                    return;
                }
                newAcls.push({ resource, name, operation: op, resource_pattern_type: pattern });
            }
            currentUser.value.kafka_acls = newAcls;
        }
    });
};

const handleSave = () => {
    if (props.user) {
        // Partial update: include only changed fields, but always include whitelist_ips and kafka_acls to prevent wiping
        const payload = {};
        if (currentUser.value.username !== props.user.username) {
            payload.username = currentUser.value.username;
        }
        if (currentUser.value.password) {
            payload.password = currentUser.value.password;
        }
        // Always include whitelist_ips and kafka_acls to prevent them from being wiped out
        payload.whitelist_ips = ipsArray.value.join(',');
        payload.kafka_acls = currentUser.value.kafka_acls.map((a) => ({
            resource: a.resource,
            name: a.name,
            operation: a.operation,
            resource_pattern_type: a.resource_pattern_type
        }));
        emit('save', payload);
    } else {
        // Creating new user: send all required fields
        const payload = {
            username: currentUser.value.username,
            password: currentUser.value.password,
            whitelist_ips: ipsArray.value.join(','),
            kafka_acls: currentUser.value.kafka_acls.map((a) => ({
                resource: a.resource,
                name: a.name,
                operation: a.operation,
                resource_pattern_type: a.resource_pattern_type
            }))
        };
        emit('save', payload);
    }
};
</script>

<style scoped>
/* Add component-specific styles if needed */
</style>
